import React, { useEffect, useState } from 'react';
import { useUI } from '../../context/UIContext';
import ResizeHandle from './ResizeHandle';
import { CodeArtifactViewer, HTMLArtifactPreview, SVGArtifactPreview, MermaidArtifactPreview, ReactArtifactPreview } from '../artifacts';

/**
 * ArtifactPanel - Right panel for displaying artifacts (code, HTML, SVG, etc.)
 *
 * Features:
 * - Slides in/out with smooth animation
 * - Shows artifact content with header
 * - Download and fullscreen buttons
 * - Resizable width (desktop only)
 * - Close button
 * - Full-screen mode with Escape key to exit
 * - Bottom sheet on mobile (SAT-82)
 */
function ArtifactPanel() {
  const {
    artifactPanelOpen,
    artifactPanelWidth,
    setArtifactPanelWidth,
    currentArtifact,
    hideArtifact,
    isEditingArtifact,
    editedArtifactContent,
    startEditingArtifact,
    updateEditedContent,
    saveArtifactChanges,
    revertArtifactChanges,
    cancelEditingArtifact,
    isArtifactFullscreen,
    toggleArtifactFullscreen,
    exitArtifactFullscreen,
    // Versioning
    artifactVersionHistory,
    currentVersionIndex,
    isViewingOldVersion,
    viewArtifactVersion,
    restoreArtifactVersion,
    goToLatestVersion,
    isMobile
  } = useUI();

  // Bottom sheet drag state for mobile
  const [bottomSheetHeight, setBottomSheetHeight] = useState(60); // percentage of viewport
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(60);

  // State for version dropdown
  const [showVersionDropdown, setShowVersionDropdown] = React.useState(false);
  const versionDropdownRef = React.useRef(null);

  // Close version dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (versionDropdownRef.current && !versionDropdownRef.current.contains(event.target)) {
        setShowVersionDropdown(false);
      }
    };

    if (showVersionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVersionDropdown]);

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isArtifactFullscreen) {
        exitArtifactFullscreen();
      }
    };

    if (isArtifactFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent scrolling on body when fullscreen
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isArtifactFullscreen, exitArtifactFullscreen]);

  // Mobile bottom sheet touch handlers (SAT-82)
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setStartHeight(bottomSheetHeight);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !isMobile) return;
    const deltaY = startY - e.touches[0].clientY;
    const deltaPercent = (deltaY / window.innerHeight) * 100;
    const newHeight = Math.min(95, Math.max(30, startHeight + deltaPercent));
    setBottomSheetHeight(newHeight);
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    setIsDragging(false);
    // Snap to closed if dragged down too much
    if (bottomSheetHeight < 35) {
      hideArtifact();
      setBottomSheetHeight(60); // Reset for next open
    }
  };

  // Prevent body scroll when bottom sheet is open on mobile
  useEffect(() => {
    if (isMobile && artifactPanelOpen) {
      document.body.style.overflow = 'hidden';
    } else if (isMobile) {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, artifactPanelOpen]);

  // Demo artifact for display
  const artifact = currentArtifact || {
    type: 'code',
    title: 'Demo Component',
    language: 'jsx',
    content: `import React from 'react';

function WelcomeMessage({ name }) {
  return (
    <div className="p-6 bg-linear-to-r from-blue-500 to-purple-600">
      <h1 className="text-2xl font-bold text-white">
        Hello, {name}!
      </h1>
      <p className="mt-2 text-gray-600">
        Welcome to DP Chat Interface.
      </p>
    </div>
  );
}

export default WelcomeMessage;`
  };

  const typeColors = {
    code: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    html: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    svg: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    react: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    mermaid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    text: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };

  // Get file extension based on artifact type and language
  const getFileExtension = (type, language) => {
    const extensionMap = {
      html: '.html',
      svg: '.svg',
      mermaid: '.mmd',
      text: '.txt',
      react: '.jsx',
      code: {
        javascript: '.js',
        js: '.js',
        typescript: '.ts',
        ts: '.ts',
        jsx: '.jsx',
        tsx: '.tsx',
        python: '.py',
        py: '.py',
        css: '.css',
        json: '.json',
        markdown: '.md',
        md: '.md',
        html: '.html',
        sql: '.sql',
        bash: '.sh',
        shell: '.sh',
        yaml: '.yaml',
        yml: '.yml',
        xml: '.xml',
        java: '.java',
        c: '.c',
        cpp: '.cpp',
        go: '.go',
        rust: '.rs',
        ruby: '.rb',
        php: '.php',
        swift: '.swift',
        kotlin: '.kt',
        default: '.txt'
      }
    };

    if (type === 'code') {
      return extensionMap.code[language?.toLowerCase()] || extensionMap.code.default;
    }
    return extensionMap[type] || '.txt';
  };

  // Get MIME type for download
  const getMimeType = (type, language) => {
    const mimeMap = {
      html: 'text/html',
      svg: 'image/svg+xml',
      mermaid: 'text/plain',
      text: 'text/plain',
      react: 'text/javascript',
      code: {
        javascript: 'text/javascript',
        js: 'text/javascript',
        typescript: 'text/typescript',
        ts: 'text/typescript',
        jsx: 'text/javascript',
        tsx: 'text/typescript',
        python: 'text/x-python',
        css: 'text/css',
        json: 'application/json',
        markdown: 'text/markdown',
        md: 'text/markdown',
        html: 'text/html',
        default: 'text/plain'
      }
    };

    if (type === 'code') {
      return mimeMap.code[language?.toLowerCase()] || mimeMap.code.default;
    }
    return mimeMap[type] || 'text/plain';
  };

  // Handle artifact download
  const handleDownload = () => {
    const content = isEditingArtifact ? editedArtifactContent : artifact.content;
    const extension = getFileExtension(artifact.type, artifact.language);
    const mimeType = getMimeType(artifact.type, artifact.language);

    // Create filename from artifact title (sanitize for filesystem)
    const safeTitle = (artifact.title || 'artifact')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .substring(0, 50);
    const filename = `${safeTitle}${extension}`;

    // Create blob and trigger download
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Mobile bottom sheet render
  if (isMobile && !isArtifactFullscreen) {
    return (
      <>
        {/* Mobile backdrop overlay */}
        {artifactPanelOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden modal-backdrop-transition animate-fadeIn"
            onClick={hideArtifact}
            aria-hidden="true"
          />
        )}

        {/* Mobile bottom sheet */}
        <aside
          className={`
            fixed inset-x-0 bottom-0 z-50 md:hidden
            flex flex-col
            bg-white dark:bg-gray-800
            rounded-t-2xl shadow-2xl
            sidebar-transition gpu-accelerated
            ${artifactPanelOpen ? 'translate-y-0' : 'translate-y-full'}
          `}
          style={{ height: `${bottomSheetHeight}vh` }}
          role="complementary"
          aria-label={`Artifact panel: ${artifact.title || 'Untitled'}`}
        >
          {/* Drag handle */}
          <div
            className="flex justify-center py-3 cursor-grab active:cursor-grabbing touch-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
          </div>

          {/* Mobile Header */}
          <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`px-2 py-1 text-xs font-medium rounded-sm ${typeColors[artifact.type] || typeColors.text}`}>
                {artifact.type?.toUpperCase()}
              </span>
              <h2 className="font-medium text-gray-900 dark:text-white truncate text-sm">
                {artifact.title || 'Untitled'}
              </h2>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Download - touch friendly size */}
              <button
                onClick={handleDownload}
                className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center
                  text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200
                  hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Download"
                aria-label="Download artifact"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>

              {/* Fullscreen - touch friendly size */}
              <button
                onClick={toggleArtifactFullscreen}
                className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center
                  text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200
                  hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Full screen"
                aria-label="View artifact in full screen"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </button>

              {/* Close - touch friendly size */}
              <button
                onClick={hideArtifact}
                className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center
                  text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200
                  hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Close"
                aria-label="Close artifact panel"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Content Area */}
          <div className="flex-1 overflow-auto">
            {artifact.type === 'code' ? (
              <CodeArtifactViewer
                content={isEditingArtifact ? editedArtifactContent : artifact.content}
                language={artifact.language}
                title={artifact.title}
                isEditing={isEditingArtifact}
                onContentChange={updateEditedContent}
              />
            ) : artifact.type === 'html' ? (
              <HTMLArtifactPreview
                content={isEditingArtifact ? editedArtifactContent : artifact.content}
                title={artifact.title}
                isEditing={isEditingArtifact}
                onContentChange={updateEditedContent}
              />
            ) : artifact.type === 'svg' ? (
              <SVGArtifactPreview
                content={isEditingArtifact ? editedArtifactContent : artifact.content}
                title={artifact.title}
                isEditing={isEditingArtifact}
                onContentChange={updateEditedContent}
              />
            ) : artifact.type === 'mermaid' ? (
              <MermaidArtifactPreview
                content={isEditingArtifact ? editedArtifactContent : artifact.content}
                title={artifact.title}
                isEditing={isEditingArtifact}
                onContentChange={updateEditedContent}
              />
            ) : artifact.type === 'react' ? (
              <ReactArtifactPreview
                content={isEditingArtifact ? editedArtifactContent : artifact.content}
                title={artifact.title}
                isEditing={isEditingArtifact}
                onContentChange={updateEditedContent}
              />
            ) : (
              <div className="p-4">
                <div className="prose dark:prose-invert max-w-none">
                  {isEditingArtifact ? editedArtifactContent : artifact.content}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Footer Actions - touch friendly */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between gap-3 safe-area-inset-bottom">
            {isEditingArtifact ? (
              <>
                <button
                  onClick={cancelEditingArtifact}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] text-sm
                    text-gray-600 dark:text-gray-300
                    hover:bg-gray-100 dark:hover:bg-gray-700
                    rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveArtifactChanges}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] text-sm
                    bg-decisiv-blue text-white
                    hover:bg-decisiv-blue/90
                    rounded-lg transition-colors font-medium"
                >
                  Save
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={startEditingArtifact}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] text-sm
                    text-gray-600 dark:text-gray-300
                    hover:bg-gray-100 dark:hover:bg-gray-700
                    rounded-lg transition-colors border border-gray-200 dark:border-gray-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
                <button
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] text-sm
                    bg-decisiv-blue/10 text-decisiv-blue
                    hover:bg-decisiv-blue/20
                    rounded-lg transition-colors"
                >
                  Re-prompt
                </button>
              </>
            )}
          </div>
        </aside>
      </>
    );
  }

  // Desktop render
  return (
    <aside
      className={`
        ${isArtifactFullscreen
          ? 'fixed inset-0 z-50 w-full h-full animate-fadeIn'
          : 'relative border-l border-gray-200 dark:border-gray-700 hidden md:flex'
        }
        flex flex-col
        bg-white dark:bg-gray-800
        artifact-panel-transition gpu-accelerated
        ${!isArtifactFullscreen && !artifactPanelOpen ? 'w-0 min-w-0 overflow-hidden' : ''}
      `}
      style={isArtifactFullscreen ? {} : {
        width: artifactPanelOpen ? `${artifactPanelWidth}px` : '0px',
        minWidth: artifactPanelOpen ? `${artifactPanelWidth}px` : '0px'
      }}
      role="complementary"
      aria-label={`Artifact panel: ${artifact.title || 'Untitled'}`}
      aria-hidden={!artifactPanelOpen}
    >
      {/* Panel content - hidden when collapsed */}
      <div className={`
        flex flex-col h-full overflow-hidden
        ${artifactPanelOpen ? 'opacity-100' : 'opacity-0'}
        transition-opacity duration-200
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`px-2 py-1 text-xs font-medium rounded-sm ${typeColors[artifact.type] || typeColors.text}`}>
              {artifact.type?.toUpperCase()}
            </span>
            <h2 className="font-medium text-gray-900 dark:text-white truncate">
              {artifact.title || 'Untitled Artifact'}
            </h2>

            {/* Version indicator and selector */}
            {artifactVersionHistory.length > 0 && (
              <div className="relative" ref={versionDropdownRef}>
                <button
                  onClick={() => setShowVersionDropdown(!showVersionDropdown)}
                  className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded
                    ${isViewingOldVersion
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }
                    hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors`}
                  title="View version history"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  v{artifactVersionHistory[currentVersionIndex]?.version || 1}
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Version dropdown */}
                {showVersionDropdown && (
                  <div className="absolute top-full left-0 mt-1 z-50 min-w-[200px] max-h-60 overflow-y-auto
                    bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                    rounded-lg shadow-lg">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        Version History ({artifactVersionHistory.length})
                      </span>
                    </div>
                    {artifactVersionHistory.map((version, index) => (
                      <button
                        key={version.version}
                        onClick={() => {
                          viewArtifactVersion(index);
                          setShowVersionDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm
                          ${index === currentVersionIndex
                            ? 'bg-decisiv-blue/10 text-decisiv-blue'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }
                          transition-colors`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">v{version.version}</span>
                          {index === artifactVersionHistory.length - 1 && (
                            <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-sm">
                              Latest
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {version.label} • {new Date(version.timestamp).toLocaleTimeString()}
                        </div>
                      </button>
                    )).reverse()}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {/* Download button */}
            <button
              onClick={handleDownload}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200
                hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Download artifact"
              aria-label="Download artifact"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>

            {/* Fullscreen button */}
            <button
              onClick={toggleArtifactFullscreen}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200
                hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={isArtifactFullscreen ? "Exit full screen (Esc)" : "Full screen"}
              aria-label={isArtifactFullscreen ? "Exit full screen mode" : "View in full screen mode"}
            >
              {isArtifactFullscreen ? (
                // Minimize/exit fullscreen icon
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
              ) : (
                // Expand/enter fullscreen icon
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>

            {/* Close button */}
            <button
              onClick={hideArtifact}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200
                hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close panel"
              aria-label="Close artifact panel"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {artifact.type === 'code' ? (
            <CodeArtifactViewer
              content={isEditingArtifact ? editedArtifactContent : artifact.content}
              language={artifact.language}
              title={artifact.title}
              isEditing={isEditingArtifact}
              onContentChange={updateEditedContent}
            />
          ) : artifact.type === 'html' ? (
            <HTMLArtifactPreview
              content={isEditingArtifact ? editedArtifactContent : artifact.content}
              title={artifact.title}
              isEditing={isEditingArtifact}
              onContentChange={updateEditedContent}
            />
          ) : artifact.type === 'svg' ? (
            <SVGArtifactPreview
              content={isEditingArtifact ? editedArtifactContent : artifact.content}
              title={artifact.title}
              isEditing={isEditingArtifact}
              onContentChange={updateEditedContent}
            />
          ) : artifact.type === 'mermaid' ? (
            <MermaidArtifactPreview
              content={isEditingArtifact ? editedArtifactContent : artifact.content}
              title={artifact.title}
              isEditing={isEditingArtifact}
              onContentChange={updateEditedContent}
            />
          ) : artifact.type === 'react' ? (
            <ReactArtifactPreview
              content={isEditingArtifact ? editedArtifactContent : artifact.content}
              title={artifact.title}
              isEditing={isEditingArtifact}
              onContentChange={updateEditedContent}
            />
          ) : (
            <div className="p-4">
              <div className="prose dark:prose-invert max-w-none">
                {isEditingArtifact ? editedArtifactContent : artifact.content}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-3 flex items-center justify-between">
          {/* Old version warning banner */}
          {isViewingOldVersion && !isEditingArtifact && (
            <div className="absolute bottom-16 left-0 right-0 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Viewing version {artifactVersionHistory[currentVersionIndex]?.version} (not latest)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => restoreArtifactVersion(currentVersionIndex)}
                  className="px-3 py-1 text-sm bg-amber-600 text-white hover:bg-amber-700 rounded-md transition-colors"
                >
                  Restore this version
                </button>
                <button
                  onClick={goToLatestVersion}
                  className="px-3 py-1 text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-md transition-colors"
                >
                  Go to latest
                </button>
              </div>
            </div>
          )}

          {isEditingArtifact ? (
            <>
              {/* Edit mode actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={cancelEditingArtifact}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm
                    text-gray-600 dark:text-gray-300
                    hover:bg-gray-100 dark:hover:bg-gray-700
                    rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </button>
                <button
                  onClick={revertArtifactChanges}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm
                    text-gray-600 dark:text-gray-300
                    hover:bg-gray-100 dark:hover:bg-gray-700
                    rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Revert
                </button>
              </div>
              <button
                onClick={saveArtifactChanges}
                className="flex items-center gap-2 px-4 py-1.5 text-sm
                  bg-decisiv-blue text-white
                  hover:bg-decisiv-blue/90
                  rounded-lg transition-colors font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Changes
              </button>
            </>
          ) : (
            <>
              {/* View mode actions */}
              <button
                onClick={startEditingArtifact}
                className="flex items-center gap-2 px-3 py-1.5 text-sm
                  text-gray-600 dark:text-gray-300
                  hover:bg-gray-100 dark:hover:bg-gray-700
                  rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>

              <button
                className="flex items-center gap-2 px-3 py-1.5 text-sm
                  bg-decisiv-blue/10 text-decisiv-blue
                  hover:bg-decisiv-blue/20
                  rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Re-prompt
              </button>
            </>
          )}
        </div>
      </div>

      {/* Resize Handle - hidden in fullscreen */}
      {artifactPanelOpen && !isArtifactFullscreen && (
        <ResizeHandle
          direction="left"
          onResize={setArtifactPanelWidth}
          minWidth={300}
          maxWidth={700}
        />
      )}
    </aside>
  );
}

export default ArtifactPanel;
